import { useEffect, useState } from 'react';
import unifiedApi from '../../utils/unifiedApi';

// Object URLs for private chat photos, kept for the session so scrolling back doesn't refetch.
const MAX_CACHED = 150;
const urlCache = new Map();

const cacheKey = (attachmentId, thumbnail) => `${attachmentId}:${thumbnail ? 'thumb' : 'full'}`;

const remember = (key, url) => {
  urlCache.set(key, url);
  if (urlCache.size > MAX_CACHED) {
    const [oldestKey, oldestUrl] = urlCache.entries().next().value;
    urlCache.delete(oldestKey);
    URL.revokeObjectURL?.(oldestUrl);
  }
};

// Exposed for tests.
export const _clearChatAttachmentCache = () => urlCache.clear();

/** Loads a private chat photo (auth header required, so <img src> can't fetch it directly). */
const useChatAttachmentUrl = (attachmentId, { thumbnail = false, enabled = true } = {}) => {
  const key = cacheKey(attachmentId, thumbnail);
  const [state, setState] = useState(() => ({ url: urlCache.get(key) || null, error: false }));

  useEffect(() => {
    if (!enabled || !attachmentId) return undefined;
    const cached = urlCache.get(key);
    if (cached) {
      setState({ url: cached, error: false });
      return undefined;
    }

    let cancelled = false;
    setState({ url: null, error: false });
    (async () => {
      try {
        const blob = await unifiedApi.getChatAttachment(attachmentId, { thumbnail });
        if (cancelled) return;
        if (!blob || !URL.createObjectURL) {
          setState({ url: null, error: true });
          return;
        }
        const url = URL.createObjectURL(blob);
        remember(key, url);
        setState({ url, error: false });
      } catch {
        if (!cancelled) setState({ url: null, error: true });
      }
    })();
    return () => { cancelled = true; };
  }, [attachmentId, thumbnail, enabled, key]);

  return state;
};

export default useChatAttachmentUrl;
